import {
  Autocomplete,
  CircularProgress,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { FrequencyTypeIds } from "constants/FrequencyTypeConstants";
import useGlobalContext from "hooks/globalData";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { FrequencyType } from "services/KnowfullyAdminClient";

type SelectFrequencyTypeProps = {
  value?: number;
  onChange: (id: number | undefined) => void;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  textFieldProps?: Partial<TextFieldProps>;
};

const SelectFrequencyType = ({
  value,
  onChange,
  label,
  disabled,
  required,
  error,
  textFieldProps,
}: SelectFrequencyTypeProps) => {
  const { formatMessage: t } = useIntl();
  const { notificationsConfigurationsService } = useGlobalContext();

  const { data, isLoading } = useQuery(
    ["notificationsConfigurationsService.getFrequencyTypes"],
    () => notificationsConfigurationsService.getFrequencyTypes(),
  );

  const options = (data?.data ?? []) as FrequencyType[];
  const selectedOption = options.find((o) => o.id === value) ?? null;

  const getLabel = (option: FrequencyType): string => {
    if (option.id === FrequencyTypeIds.RepeatEvery)
      return t({ id: "repeatEvery" });
    return option.name || "";
  };

  return (
    <Autocomplete<FrequencyType>
      value={selectedOption}
      onChange={(_event, newValue) => onChange(newValue?.id)}
      options={options}
      loading={isLoading}
      disabled={disabled || isLoading}
      getOptionLabel={getLabel}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          variant="standard"
          label={label || t({ id: "frequency" })}
          required={required}
          error={error}
          InputProps={{
            ...params.InputProps,
            startAdornment: isLoading && <CircularProgress size={16} />,
          }}
        />
      )}
    />
  );
};

export default SelectFrequencyType;
